import { BaseMeiucaService } from './MeiucaService'
import { env, apiEndpoints } from '@/lib/env'
import type { User, UserPreferences } from '@/types/auth'

export interface UserSearchResult {
  id: string
  name: string
  nickname?: string
  phoneNumber?: string
  avatar?: string
}

interface UserSearchResponse {
  data: UserSearchResult[]
}

// Re-export UserPreferences from auth types for convenience
export type { UserPreferences }

interface UserPreferencesResponse {
  data: UserPreferences
}

interface UserProfileResponse {
  data: {
    profile: User
    preferences: UserPreferences
  }
}

class UserService extends BaseMeiucaService {
  constructor() {
    super({
      baseURL: env.API_URL,
      refreshEndpoint: apiEndpoints.auth.refresh,
      cookiePrefix: '', // No prefix for meiuca-app (keeps original behavior)
      debug: env.IS_DEVELOPMENT,
      onAuthenticationError: () => {
        // Use replace to prevent back button issues - redirect to root
        if (typeof window !== 'undefined') {
          window.location.replace('/')
        }
      }
    })
  }

  // Get user profile with preferences
  async getProfile(): Promise<User> {
    try {
      console.log('👤 Fetching user profile with preferences')

      const response = await this.get<UserProfileResponse>('/users/me')

      // Backend returns { profile, preferences } separately
      // Merge preferences into profile for consistency with login/register responses
      const user: User = {
        ...response.data.data.profile,
        preferences: response.data.data.preferences
      }

      console.log('✅ User profile loaded:', user.id)

      return user

    } catch (error) {
      console.error('❌ Error fetching user profile:', error)
      throw error
    }
  }

  // Busca usuários por query (nome, nickname, telefone)
  async searchUsers(query: string): Promise<UserSearchResult[]> {
    try {
      console.log(`🔍 Searching users with query: "${query}"`)

      if (!query.trim()) {
        return []
      }

      const response = await this.get<UserSearchResponse>(`/users?query=${encodeURIComponent(query.trim())}`)

      console.log('✅ Users found:', response.data.data.length)

      return response.data.data

    } catch (error) {
      throw error
    }
  }

  // Get user preferences
  async getUserPreferences(): Promise<UserPreferences> {
    try {
      console.log('📋 Fetching user preferences')

      const response = await this.get<UserPreferencesResponse>('/users/me/preferences')

      console.log('✅ User preferences loaded:', response.data.data)

      return response.data.data

    } catch (error) {
      console.error('❌ Error fetching user preferences:', error)
      throw error
    }
  }

  // Save user preferences (partial update)
  async saveUserPreferences(preferences: UserPreferences): Promise<UserPreferences> {
    try {
      console.log('💾 Saving user preferences:', preferences)

      const response = await this.patch<UserPreferencesResponse>('/users/me/preferences', preferences)

      console.log('✅ User preferences saved successfully')

      return response.data.data

    } catch (error) {
      console.error('❌ Error saving user preferences:', error)
      throw error
    }
  }
}

// Export singleton instance
export const userService = new UserService()
